import clsx from 'clsx'
import { useState } from 'preact/hooks'

import { scrollHistoryManager } from '@/components/helpers/scroll-history'
import { sizeUnitDisplay } from '@/components/helpers/size-units.mjs'
import { FIELD_BOX_CLASS, FIELD_CAPTION_CLASS } from '@/components/snippets/cart-line-select/cart-line-select'

import { useBeaconLogClick } from '../hooks/useBeaconAnalytics'
import { Price } from './Price'
import { ResultImage } from './ResultImage'

// The image box is 100px wide (120px at md+), so the ladder tops out near 3x that.
const UPSELL_IMAGE_WIDTHS = [120, 240, 360]
const UPSELL_IMAGE_SIZES = '(min-width: 1024px) 120px, 100px'

// Athos variants carry `available`; `available_for_sale` is the Shopify shape GWP passes in.
const isAvailable = (variant) => {
  const flag = variant?.available ?? variant?.available_for_sale
  return flag !== false && flag !== 'false'
}

const US_SIZE = /\bUS\s+0*(\d+(?:\.\d+)?)/i

/** Athos sends one combined string per variant ("EURO 26/KIDS US 08/KIDS UK 08.5"); show the US value as a cart line does. */
const getSizeLabel = (variant) => {
  const athosOptions = variant?.options && typeof variant.options === 'object' ? Object.entries(variant.options) : []
  const athosOption = athosOptions.find(([name]) => /size|value/i.test(name)) ?? athosOptions[0]
  const sizeOption = variant?.selected_options?.find((option) => /size|value/i.test(option?.name ?? ''))
  const raw = athosOption?.[1]?.value ?? sizeOption?.value ?? variant?.option1 ?? variant?.title
  const label = raw != null ? String(raw).trim() : ''
  if (!label) return 'N/A'

  const us = label.match(US_SIZE)
  if (us) return sizeUnitDisplay({ us: us[1] }, 'US')
  return label.replace(/\b0+(\d)/g, '$1')
}

/**
 * Shares the cart line dropdowns' field styling (cart-line-select.js) so the upsell size box and a
 * line item's size box are the same control. No chevron of its own: @tailwindcss/forms already puts
 * one on every select as a background image, and adding an icon rendered two.
 */
function SizeSelect({ variants, selectedVariant, onSelect, optionName }) {
  return (
    <label className="relative block w-full grow">
      <span className={FIELD_CAPTION_CLASS}>{optionName}</span>
      <select
        className={FIELD_BOX_CLASS}
        value={selectedVariant ? String(selectedVariant.id) : ''}
        onChange={(event) => {
          const { value } = event.currentTarget
          onSelect(variants.find((variant) => String(variant.id) === value) || null)
        }}
        aria-label={`Select ${optionName}`}
      >
        <option value="" disabled>
          Select
        </option>
        {variants.map((variant) => {
          const available = isAvailable(variant)
          return (
            <option key={variant.id} value={variant.id} disabled={!available}>
              {getSizeLabel(variant)}
              {available ? '' : ' - Sold Out'}
            </option>
          )
        })}
      </select>
    </label>
  )
}

/**
 * Horizontal product card with an inline size selector and add button, selected with
 * `card="upsell"`. `options.onAddToCart` / `options.priceOverride` are kept for parity with
 * Basecamp's GWP block, which renders this card directly.
 */
export function ResultUpsellCard({ result, className, dataIndex = '0', options = {} }) {
  const atcText = options.atcText || 'Add To Bag'
  const soldOutText = options.soldOutText || 'Sold Out'

  const variants = Array.isArray(result.variants) ? result.variants : []
  const [firstVariant] = variants

  const isGiftCard = result.is_giftcard === 'true' || result.template_suffix === 'gift-card'
  const optionName = isGiftCard ? 'Value' : 'Size'

  const hasSingleVariant = variants.length <= 1
  const anyAvailable = variants.some(isAvailable)

  const [selectedVariant, setSelectedVariant] = useState(null)
  const [loading, setLoading] = useState(false)

  const clickLogger = useBeaconLogClick()

  const handleLogClick = () => {
    scrollHistoryManager.updateHistoryStateWithCollectionInfo('recommendation', result, undefined, '')
    clickLogger(result)
  }

  const addToCart = async (variant) => {
    if (!variant) return

    setLoading(true)
    try {
      if (options.onAddToCart) {
        await options.onAddToCart(variant)
      } else if (window.Cart) {
        const id = typeof variant.id === 'string' ? parseInt(variant.id, 10) : variant.id
        // silent: the drawer is already open, and updateCartCount then refetches this carousel.
        await window.Cart.addLineItems({ id, quantity: 1 }, { silent: true })
      }
    } catch (error) {
      console.error('Error adding upsell item to cart:', error)
    } finally {
      setLoading(false)
    }
  }

  const buttonClass = 'btn btn-sm btn-solid-black shrink-0 justify-center'

  const renderAtc = () => {
    if (!anyAvailable) {
      return (
        <button className={clsx(buttonClass, 'w-full')} aria-label={soldOutText} disabled>
          <span>{soldOutText}</span>
        </button>
      )
    }

    if (hasSingleVariant) {
      return (
        <button
          type="button"
          className={clsx(buttonClass, 'w-full')}
          onClick={() => addToCart(firstVariant)}
          disabled={loading}
          aria-label={atcText}
        >
          <span>{atcText}</span>
        </button>
      )
    }

    return (
      <div className="flex items-center gap-2">
        <SizeSelect
          variants={variants}
          selectedVariant={selectedVariant}
          onSelect={setSelectedVariant}
          optionName={optionName}
        />
        {selectedVariant ? (
          <button
            type="button"
            className={clsx(buttonClass, 'w-[72px]')}
            onClick={() => addToCart(selectedVariant)}
            disabled={loading}
            aria-label={atcText}
          >
            <span>Add</span>
          </button>
        ) : (
          <button className={clsx(buttonClass, 'w-[72px]')} aria-label="Select a size" disabled>
            <span>Select</span>
          </button>
        )}
      </div>
    )
  }

  const { priceOverride } = options
  const price = priceOverride
    ? (priceOverride.price / 100).toFixed(2)
    : String(firstVariant?.price ?? result.price ?? '')
  const compareAtPriceValue = priceOverride
    ? priceOverride.compareAtPrice != null
      ? (priceOverride.compareAtPrice / 100).toFixed(2)
      : undefined
    : (firstVariant?.compare_at_price ?? result.compare_at_price ?? undefined)

  return (
    <div
      className={clsx('flex items-stretch bg-primary-white rounded-lg', className)}
      data-dl-track="select_item"
      data-dl-product-id={result.uid}
      data-dl-index={dataIndex}
    >
      <a
        href={result.url}
        onClick={handleLogClick}
        className="relative aspect-[110/120] w-[100px] shrink-0 md:w-[120px] p-3"
        aria-label={result.name}
      >
        <ResultImage result={result} sizes={UPSELL_IMAGE_SIZES} widths={UPSELL_IMAGE_WIDTHS} />
      </a>

      <div className="flex min-w-0 grow flex-col justify-between gap-y-3 px-3 py-2">
        <div className="flex min-w-0 flex-col gap-y-1">
          <p className="paragraph-sm font-bold" dangerouslySetInnerHTML={{ __html: result.brand ?? '' }} />
          <a
            href={result.url}
            onClick={handleLogClick}
            className="paragraph-sm line-clamp-1"
            dangerouslySetInnerHTML={{
              __html: result.mfield_utopia_customer_facing_product_name ?? result.name ?? ''
            }}
          />
          <Price price={price} compareAtPrice={compareAtPriceValue ? String(compareAtPriceValue) : undefined} />
        </div>

        {renderAtc()}
      </div>
    </div>
  )
}
